import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { GA4_GROUP_BY, type Ga4GroupBy, type Ga4TrafficQuery } from '@/api/types'
import { resolvePeriod, type GlobalFilters } from '@/lib/filters'

const DEFAULT_GROUP_BY: Ga4GroupBy = 'channel'
const PARAM = 'group_by'

function isGroupBy(value: string | null): value is Ga4GroupBy {
  return (GA4_GROUP_BY as readonly string[]).includes(value ?? '')
}

/** The grouping lives in the URL beside the global filters, so a copied link reopens the same table. */
export function useGroupBy(): [Ga4GroupBy, (next: Ga4GroupBy) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get(PARAM)
  const groupBy = isGroupBy(raw) ? raw : DEFAULT_GROUP_BY
  const set = (next: Ga4GroupBy) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === DEFAULT_GROUP_BY) updated.delete(PARAM)
        else updated.set(PARAM, next)
        return updated
      },
      { replace: true },
    )
  return [groupBy, set]
}

/**
 * What the page asks GA4 for: the global filter bar's date range, and its
 * comparison when that is "previous period" (the only one GA4 traffic offers).
 * The CSV link is built from this same query, so the download is the table.
 */
export function useTrafficQuery(filters: GlobalFilters, groupBy: Ga4GroupBy): Ga4TrafficQuery {
  // `filters` is memoised on the URL's query string, so it changes exactly when the range or comparison does.
  return useMemo(() => {
    const { current } = resolvePeriod(filters, new Date())
    return { from: current.from, to: current.to, groupBy, compare: filters.compare === 'previous_period' }
  }, [filters, groupBy])
}
